import { Button, EButtonColorVariant, EHelpTextVariant, HelpText } from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import FlexiblePayment from '@/assets/gps/flexible-payment.jpg';
import GPSBannerImg from '@/assets/gps/gps_banner.png';
import HistoryAndReporting from '@/assets/gps/history-and-reporting.png';
import OnTopOfMaintanance from '@/assets/gps/on-top-of-maintanace.jpg';
import RealtimeLocation from '@/assets/gps/realtime-location.jpg';
import TrackAnyRig from '@/assets/gps/track-any-rig.png';
import ZubieLogo from '@/assets/gps/zubie-logo.png';
import ZubieScreen from '@/assets/gps/zubie-screens.png';
import { AccountFormTemplate } from '@/components/auth/AccountFormTemplate';
import { IProps, LoginForm } from '@/components/auth/LoginForm';
import { CHECK } from '@/components/switchback/Icon/assets';
import Icon, { IconSize } from '@/components/switchback/Icon/IconComponent';
import NewsSlider from '@/components/templates/gps/NewsSlider';

import css from './GPSContent.module.css';

interface IGPSProps {
  openZubieCheckout: () => void;
  triggerLoginModal: (waitTime?: number) => void;
  loginFormProps: ILoginProps;
}

export interface ILoginProps extends IProps {
  isAuthenticated: boolean;
}

const GPSContent: React.FC<IGPSProps> = ({
  openZubieCheckout,
  triggerLoginModal,
  loginFormProps,
}) => {
  const intl = useIntl();
  const [isOpeningZubie, setOpeningZubie] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const zubieSliderItems = [
    {
      title: intl.formatMessage({ defaultMessage: 'Real-time location', id: 'myRxr2' }),
      description: intl.formatMessage({
        defaultMessage: 'Easily see where your rig is using Zubie’s website or mobile app.',
        id: '5v1b0b',
      }),
      descriptionBullets: [
        intl.formatMessage({
          defaultMessage:
            'Geofencing alerts if your vehicle goes into an area you’ve marked as restricted.',
          id: 'FIHqch',
        }),
        intl.formatMessage({
          defaultMessage:
            'Share your vehicle’s location in case of a breakdown or other emergency.',
          id: '1rVxSd',
        }),
      ],
      img: RealtimeLocation.src,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Track any rig', id: 'k7C/0q' }),
      description: intl.formatMessage({
        defaultMessage: 'GPS devices for both drivable and towable RVs.',
        id: 'otPig5',
      }),
      descriptionBullets: [
        intl.formatMessage({
          defaultMessage:
            'Devices that plug into your diagnostic port or battery, or magnetic ones.',
          id: '1ndu4f',
        }),
        intl.formatMessage({
          defaultMessage: 'Buy your devices at a special rate, or lease them.',
          id: 'tusGHP',
        }),
      ],
      img: TrackAnyRig.src,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Stay on top of maintenance', id: 'tdSWzK' }),
      description: intl.formatMessage({
        defaultMessage: 'Know when you’re due for a service.',
        id: '7p8ITW',
      }),
      descriptionBullets: [
        intl.formatMessage({
          defaultMessage: 'Mileage-based maintenance reminders.',
          id: '4Gf6dG',
        }),
        intl.formatMessage({
          defaultMessage: 'Discounts at a national network of service shops.',
          id: 'rjjR68',
        }),
      ],
      img: OnTopOfMaintanance.src,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Trip history and reporting', id: 'yLVXxr' }),
      description: intl.formatMessage({
        defaultMessage: 'Track your rig’s mileage and depreciation for taxes.',
        id: 'IjD1Id',
      }),
      descriptionBullets: [
        intl.formatMessage({
          defaultMessage: 'A variety of reports based on time intervals.',
          id: 'wn0t9o',
        }),
        'Tag trips as business or personal.',
      ],
      img: HistoryAndReporting.src,
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Flexible payment and seasonal use',
        id: 'BYnquS',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'There’s no need to be locked in to a contract when your rig is sitting in storage. Zubie allows you to pause a subscription and only pay for the months that you actually need the service.',
        id: 'xi4KrL',
      }),
      img: FlexiblePayment.src,
    },
  ];

  const onGetStartedWithZubie = async () => {
    setOpeningZubie(true);
    try {
      await openZubieCheckout();
      setErrorMessage('');
    } catch (e) {
      setErrorMessage(e);
    } finally {
      setOpeningZubie(false);
    }
  };

  return (
    <>
      <div className={css.container}>
        <div className={css.bannerImage}>
          <Image
            alt="GPS tracking for your rig"
            src={GPSBannerImg}
            role="presentation"
            layout="responsive"
            height={353}
            width={947}
            priority={true}
          />
        </div>
        <div className={`${css.hero} ${css.content}`}>
          <div className={css.verticalDivider + ' ' + css.contentLeft}>
            <h2 className="h2">
              {intl.formatMessage({ defaultMessage: 'Track your rig with Zubie', id: 'H46+Tl' })}
            </h2>
            <div className={css.contentLeftText}>
              {intl.formatMessage({
                defaultMessage:
                  'No more wondering where your rental is. Know where it is, with Zubie.',
                id: 'Eqf6Lv',
              })}
            </div>
            <div className={css.contentLeftText}>
              {intl.formatMessage({
                defaultMessage:
                  'This peace of mind even comes with a special rate. Just for being part of the Outdoorsy family, you’ll save on both GPS devices and on monthly subscriptions.',
                id: 'p/H10n',
              })}
            </div>
          </div>
          <div className={css.contentRight}>
            {!loginFormProps.isAuthenticated && (
              <AccountFormTemplate className="max-w-sm px-5 lg:px-8" isPage>
                <div>
                  <h2 className="mb-10 h4">
                    {intl.formatMessage({
                      defaultMessage: 'Log in to Outdoorsy and claim your special rate',
                      id: 'lNbxVR',
                    })}
                  </h2>
                  <LoginForm {...loginFormProps} />
                </div>
              </AccountFormTemplate>
            )}
            {loginFormProps.isAuthenticated && (
              <div className="">
                <h4 className="mb-10 h4">
                  {intl.formatMessage({
                    defaultMessage: 'Start tracking today and save',
                    id: '4WpuN6',
                  })}
                </h4>
                <Button
                  className="lg:mt-10"
                  variant={EButtonColorVariant.Primary}
                  label={intl.formatMessage({
                    defaultMessage: 'Get started with Zubie',
                    id: 'Cstp+0',
                  })}
                  loading={isOpeningZubie}
                  disabled={false}
                  type="submit"
                  fullWidth
                  onClick={onGetStartedWithZubie}
                />
                {errorMessage && (
                  <HelpText
                    className="mt-2 text-400"
                    text={errorMessage}
                    variant={EHelpTextVariant.Error}
                    showIcon
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className={css.horizontalDivider + ' lg:hidden'} />
      </div>

      <div className={css.contentWrapper}>
        <div className={`${css.hero} ${css.whyZubie}`}>
          <div className={css.whyZubieSection + ' ' + css.whyZubieSectionLeft}>
            <Image
              alt="Zubie"
              src={ZubieLogo}
              role="presentation"
              layout="intrinsic"
              height={87}
              width={240}
            />
            <h2 className="h2">
              {intl.formatMessage({ defaultMessage: 'Why track with Zubie?', id: 'j+ej/h' })}
            </h2>
          </div>
          <div className={css.whyZubieSection}>
            <ul>
              <li key="zubie-check-1" className={css.whyZubieCheckItem}>
                <Icon name={CHECK} size={IconSize.normal} />
                <span className="text-gray-800">
                  {intl.formatMessage({
                    defaultMessage: 'Website or mobile app tracking',
                    id: 'WZHKEe',
                  })}
                </span>
              </li>
              <li key="zubie-check-2" className={css.whyZubieCheckItem}>
                <Icon name={CHECK} size={IconSize.normal} />
                <span className="text-gray-800">
                  {intl.formatMessage({ defaultMessage: 'Devices for any rig', id: 'yqxxFC' })}
                </span>
              </li>
              <li key="zubie-check-3" className={css.whyZubieCheckItem}>
                <Icon name={CHECK} size={IconSize.normal} />
                <span className="text-gray-800">
                  {intl.formatMessage({
                    defaultMessage: 'Maintenance network and reminders',
                    id: 'g22cFM',
                  })}
                </span>
              </li>
              <li key="zubie-check-4" className={css.whyZubieCheckItem}>
                <Icon name={CHECK} size={IconSize.normal} />
                <span className="text-gray-800">
                  {intl.formatMessage({
                    defaultMessage: 'Trip history and reporting',
                    id: 'yLVXxr',
                  })}
                </span>
              </li>
              <li key="zubie-check-5" className={css.whyZubieCheckItem}>
                <Icon name={CHECK} size={IconSize.normal} />
                <span className="text-gray-800">
                  {intl.formatMessage({ defaultMessage: 'Seasonal subscriptions', id: '++bLZ8' })}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className={css.horizontalDivider} />
        <div className={`${css.hero} ${css.sliderContainer}`}>
          <NewsSlider items={zubieSliderItems} />
        </div>
        <div className={css.horizontalDivider} />
        <div className={`${css.hero} ${css.howItWorks}`}>
          <h2 className="h2">See how it works</h2>
          <div className={css.aspectRatio}>
            <iframe
              title="Some title"
              src="https://fast.wistia.net/embed/iframe/qpx8mc6q1g?controlsVisibleOnLoad=true&playerColor=688AAD&version=v1&wmode=transparent"
              frameBorder="0"
              scrolling="no"
            />
          </div>
        </div>

        <div className={`${css.hero} ${css.getStarted}`}>
          <div className={css.getStartedLeft}>
            <Image
              alt="Zubie app screens"
              src={ZubieScreen}
              role="presentation"
              layout="intrinsic"
              objectFit="scale-down"
              height={236}
              width={351}
            />
          </div>
          <div className={css.getStartedRight}>
            <h4 className="h4">
              {intl.formatMessage({ defaultMessage: 'Get started with Zubie', id: 'Cstp+0' })}
            </h4>
            <div className="mt-5 mb-10 text-gray-500">
              {intl.formatMessage({
                defaultMessage:
                  'Get peace of mind with multiple tracking options available through Zubie’s website and mobile app. Flexible payment options allow you to pay for tracking only when your rig is in use.',
                id: 'lAo7OL',
              })}
            </div>
            {!loginFormProps.isAuthenticated && (
              <Button
                variant={EButtonColorVariant.Primary}
                label={intl.formatMessage({
                  defaultMessage: 'Log in and get started with Zubie',
                  id: 'igsgbS',
                })}
                loading={false}
                disabled={false}
                type="submit"
                onClick={() => triggerLoginModal(0)}
              />
            )}
            {loginFormProps.isAuthenticated && (
              <Button
                variant={EButtonColorVariant.Primary}
                label={intl.formatMessage({
                  defaultMessage: 'Get started with Zubie',
                  id: 'Cstp+0',
                })}
                loading={isOpeningZubie}
                disabled={false}
                type="submit"
                onClick={onGetStartedWithZubie}
              />
            )}
            {errorMessage && (
              <HelpText
                className="mt-2 text-400"
                text={errorMessage}
                variant={EHelpTextVariant.Error}
                showIcon
              />
            )}
          </div>
        </div>
      </div>
      <div className={css.contentWrapper}>
        <div id="zubie" className={`${css.hero} ${css.feedback}`} />
      </div>
    </>
  );
};

export default GPSContent;
